import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Modal, Form, Space, Typography, Row, Col, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  AppstoreOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  UserOutlined
} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import {
  useCreateAppointmentMutation,
  useCreateServiceAppointmentMutation,
  useListResourcesQuery,
  useListServicesQuery,
  type CreateAppointmentRequest,
  type CreateClientAppointmentRequest,
  type CreateClientServiceAppointmentRequest,
  type CreateServiceAppointmentRequest
} from '@/api/mcp-appointments';
import type { CreateAppointmentDialogProps, CreateAppointmentDialogInitialValues } from './types';
import { DEFAULT_DURATION_MINUTES } from './constants';
import { getApiErrorMessage } from '@/utils/apiError';
import { useAppointmentSlotSelection, useServiceAppointmentSlotSelection } from '../slot-scheduling';
import {
  filterAppointmentServices,
  filterResourcesForDirectBooking,
  resolveServiceRequiredUserInfo,
  getEffectiveFulfillmentMode,
  serviceNeedsPrimaryResource
} from '@/utils/serviceBooking';
import { toE164Phone } from '@/utils/phoneValidation';
import { useModuleExtension } from '@/contexts/ModuleContext';
import { bindAppointmentExtension } from '../extensions/bindAppointmentExtension';
import type { ParsedAppointmentContext } from '../extensions/extension-utils';
import { AppointmentDialogSlot, type ModuleAppointmentExtension } from '../extensions/extension-types';
import { ConflictAlerts } from './components/ConflictAlerts';
import { ResourceSelector } from './components/ResourceSelector';
import { DurationInput } from './components/DurationInput';
import { SlotDateTimePicker } from './components/SlotDateTimePicker';
import { TimePreview } from './components/TimePreview';
import { AppointmentTypeSelector } from './components/AppointmentTypeSelector';
import { RegisteredCustomerSelector } from './components/RegisteredCustomerSelector';
import { GuestDetails } from './components/GuestDetails';
import { NotesField } from './components/NotesField';
import { FormActions } from './components/FormActions';
import { FormSection } from './components/FormSection';
import { ReminderToggle } from './components/ReminderToggle';
import {
  BookingModeSelector,
  type AppointmentBookingMode
} from './components/BookingModeSelector';
import { ServiceSelector } from './components/ServiceSelector';
import { ServiceBookingSummary } from './components/ServiceBookingSummary';
import { PrimaryResourceSelector } from './components/PrimaryResourceSelector';
import { ServiceBookingEmptyAlert } from './components/ServiceBookingEmptyAlert';
import { DirectResourceBookingEmptyAlert } from './components/DirectResourceBookingEmptyAlert';
import { AppointmentFormatSelector, generateJitsiMeetingLinks } from '../extensions/jobInterview';
import useAppNotification from '@/hooks/useAppNotification';
import { deepTrimStrings } from '@/utils/deepTrimStrings';
import { useReminder } from '@/contexts/ReminderContext';
import type { AppointmentFormat } from '@/api/mcp-appointments/types/appointments';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveClientId } from '@/contexts/AdminClientsContext';
import { useClientCustomersQuery } from '@/api/auth/queries';

function mapFormFormatToApi(format: unknown): AppointmentFormat {
  return format === 'video' ? 'VIDEO' : 'IN_PERSON';
}

function resolveInitialBookingMode(
  explicit: AppointmentBookingMode | undefined,
  hasActiveServices: boolean
): AppointmentBookingMode {
  if (explicit === 'resource') return 'resource';
  if (explicit === 'service') return hasActiveServices ? 'service' : 'resource';
  return hasActiveServices ? 'service' : 'resource';
}

/**
 * @file Create-appointment modal: wires slot scheduling, optional module extensions, and MCP create.
 */

const { Text } = Typography;

/** Responsive modal width: full width on phones, two-column layout from `lg` up. */
const MODAL_WIDTH = { xs: '100%', sm: '100%', md: 720, lg: 1080, xl: 1200, xxl: 1280 };

/**
 * Modal form for creating a tenant appointment: bookable resource, policy-driven duration,
 * slot-backed start time, guest or registered-user payload, and optional notes.
 *
 * **Callers** pass `initialValues` (see `CreateAppointmentDialogProps` in `./types`) for context —
 * for example calendar filter `resourceId`, day-only `preferredDate`, or job applicant fields.
 * Those values are snapshotted while the dialog is open so parent re-renders do not overwrite the
 * form mid-session.
 *
 * **Scheduling** is handled by `useAppointmentSlotSelection`: availability and disabled
 * dates/times come from the appointments API; `ConflictAlerts` surfaces slot and truncation issues.
 * Submit stays disabled until the user commits a valid slot (`hasCommittedStartTime`).
 *
 * **Modules** (`moduleId` / `moduleData`) may inject UI at `AppointmentDialogSlot` positions and
 * supply default fields via `getInitialValues`. Extension defaults are merged first; caller
 * `initialValues` always win for resource, time, and guest fields (e.g. manage calendar preselect).
 */
const CreateAppointmentDialog: React.FC<CreateAppointmentDialogProps> = ({
  visible,
  onCancel,
  initialValues,
  metadata,
  showConfirmSwitch = false,
  onSuccess,
  onError,
  showResourceSelector = true,
  showAppointmentType = true,
  showNotes = true,
  title,
  resetOnClose = true,
  moduleId,
  moduleData
}) => {
  const { showSuccessMessage, showErrorMessage } = useAppNotification();
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');
  const [form] = Form.useForm();
  const [duration, setDuration] = useState<number>(DEFAULT_DURATION_MINUTES);
  const [appointmentType, setAppointmentType] = useState<'user' | 'anonymous'>('anonymous');
  const [createAsConfirmed, setCreateAsConfirmed] = useState<boolean>(false);
  const [bookingMode, setBookingMode] = useState<AppointmentBookingMode>('resource');
  const [isPreparingVideoLinks, setIsPreparingVideoLinks] = useState(false);
  const [isSubmitWorkflowRunning, setIsSubmitWorkflowRunning] = useState(false);
  const submitInFlightRef = useRef(false);
  const { openCreate: openReminderCreate } = useReminder();
  const { payload: currentUser } = useAuth();
  const effectiveClientId = useEffectiveClientId();

  /**
   * Bumped once per dialog open so `SlotDateTimePicker` internal state resets with the modal session.
   */
  const [dialogKey, setDialogKey] = useState<number>(0);

  /**
   * Calendar day passed as `preferredDate` in `initialValues`; forwarded to slot selection so the
   * picker opens on that day without treating the time as committed until the user picks a slot.
   */
  const [preferredDateWhenOpened, setPreferredDateWhenOpened] = useState<Dayjs | undefined>(undefined);

  /**
   * Gate for the open-phase `useLayoutEffect`: run merge + `setFieldsValue` once per open, not on
   * every re-render while `visible` stays true (e.g. React Query invalidating the calendar).
   */
  const hasInitializedForOpenRef = useRef(false);

  /**
   * Snapshot of `initialValues` for the current open session. Captured once as the modal opens so
   * later parent renders cannot replace the caller intent mid-session.
   */
  const initialValuesWhenOpenedRef = useRef<CreateAppointmentDialogInitialValues | undefined>(undefined);
  const hasCapturedOpenValuesRef = useRef(false);
  if (visible && !hasCapturedOpenValuesRef.current) {
    initialValuesWhenOpenedRef.current = initialValues;
    hasCapturedOpenValuesRef.current = true;
  }

  /** Day-plus context available on the first open render (avoids waiting for preferredDateWhenOpened state). */
  const effectivePreferredDate =
    preferredDateWhenOpened
    ?? (visible ? initialValuesWhenOpenedRef.current?.preferredDate?.startOf('day') : undefined);

  const createAppointmentMutation = useCreateAppointmentMutation();
  const createServiceAppointmentMutation = useCreateServiceAppointmentMutation();
  const { data: resources, isLoading: resourcesLoading } = useListResourcesQuery(undefined, true);
  const {
    data: services,
    isLoading: servicesLoading,
    isPending: servicesPending
  } = useListServicesQuery({ isActive: true }, visible);
  const appointmentServices = useMemo(
    () => filterAppointmentServices(services),
    [services]
  );

  const isServiceMode = bookingMode === 'service';
  const showServiceModeOption = appointmentServices.length > 0;

  const directBookableResources = useMemo(
    () => filterResourcesForDirectBooking(resources, appointmentServices),
    [resources, appointmentServices]
  );

  const resourceSelectorLoading = resourcesLoading || servicesLoading;

  const showDirectResourceBookingEmpty =
    !isServiceMode
    && (resources?.length ?? 0) > 0
    && directBookableResources.length === 0
    && appointmentServices.length > 0
    && !resourceSelectorLoading;

  const resourceId = Form.useWatch('resourceId', form);
  const serviceId = Form.useWatch('serviceId', form);
  const selectedUserId = Form.useWatch('userId', form);
  const startTime = Form.useWatch('startTime', form);
  const formDurationRaw = Form.useWatch('duration', form) ?? duration;

  /** Clamped duration in minutes for slot queries and preview (5–480, default constant). */
  const formDuration = useMemo(() => {
    const n = Number(formDurationRaw);
    return (Number.isInteger(n) && n >= 5 && n <= 480) ? n : DEFAULT_DURATION_MINUTES;
  }, [formDurationRaw]);
  const appointmentFormat = Form.useWatch('appointmentFormat', form);
  const allFormValues = Form.useWatch([], form) || {};

  const selectedService = useMemo(
    () => appointmentServices.find((s) => s.id === serviceId),
    [appointmentServices, serviceId]
  );

  const serviceDuration = selectedService?.durationMinutes ?? DEFAULT_DURATION_MINUTES;
  const effectiveDuration = isServiceMode ? serviceDuration : formDuration;

  const resourceSlotSelection = useAppointmentSlotSelection({
    form,
    resourceId,
    duration: formDuration,
    visible: visible && !isServiceMode,
    preferredDate: effectivePreferredDate
  });

  const serviceSlotSelection = useServiceAppointmentSlotSelection({
    form,
    service: selectedService,
    visible: visible && isServiceMode,
    preferredDate: effectivePreferredDate
  });

  const activeSlot = isServiceMode ? serviceSlotSelection : resourceSlotSelection;

  const {
    disabledDate,
    disabledTime,
    isCheckingSlots,
    isLoadingVisibleMonthSlots,
    isCheckingSelectedMonthSlots,
    isSelectedSlotValid,
    displayedSlotUnavailable,
    truncated,
    slotAlignmentMinutes,
    hasCommittedStartTime,
    setHasCommittedStartTime,
    preferredPickerValue,
    slotsVisibleMonth,
    handleSlotsVisibleMonthChange,
    handleCalendarDaySelect,
    initializeForOpen: initializeResourceSlotsForOpen,
    resetForClose: resetResourceSlotsForClose,
    setDismissedSlotUnavailable,
    slotsByDateKey,
    timezone
  } = activeSlot;

  const resourcePolicy = isServiceMode ? undefined : resourceSlotSelection.resourcePolicy;

  const resetForClose = useCallback(() => {
    resetResourceSlotsForClose();
    serviceSlotSelection.resetForClose();
  }, [resetResourceSlotsForClose, serviceSlotSelection.resetForClose]);

  const serviceRequiredUserInfo = useMemo(
    () =>
      selectedService
        ? resolveServiceRequiredUserInfo(
            selectedService.requiredUserInfo,
            getEffectiveFulfillmentMode(selectedService)
          )
        : undefined,
    [selectedService]
  );
  const customersQuery = useClientCustomersQuery(
    effectiveClientId ?? '',
    appointmentType === 'user' && !!effectiveClientId
  );
  const selectedRegisteredCustomer = useMemo(
    () => customersQuery.data?.users.find((user) => user.id === selectedUserId),
    [customersQuery.data?.users, selectedUserId]
  );

  const showPrimaryResourceSelector =
    isServiceMode && selectedService && serviceNeedsPrimaryResource(selectedService);

  // DisabledTimeConfig is compatible with Ant Design picker; SlotDateTimePicker types it loosely.
  const slotPickerDisabledTime = isServiceMode
    ? serviceSlotSelection.disabledTime
    : resourceSlotSelection.disabledTime;

  /**
   * Stable input for {@link bindAppointmentExtension}: current form instance, flattened values
   * (including `appointmentFormat` from extensions), module payload, and submit loading flag.
   */
  const extensionContext = useMemo(() => ({
    form,
    formValues: { ...allFormValues, appointmentFormat },
    moduleData,
    isSubmitting:
      isSubmitWorkflowRunning ||
      createAppointmentMutation.isPending ||
      createServiceAppointmentMutation.isPending
  }), [
    form,
    allFormValues,
    appointmentFormat,
    moduleData,
    isSubmitWorkflowRunning,
    createAppointmentMutation.isPending,
    createServiceAppointmentMutation.isPending
  ]);

  const extension = useModuleExtension<ModuleAppointmentExtension>(moduleId, 'appointments');
  const {
    getSlotComponent,
    buildContext: buildExtensionContext,
    validate: validateExtension,
    getInitialValues: getExtensionInitialValues,
    onBeforeAppointmentCreated: handleExtensionBeforeAppointmentCreated,
    onAppointmentCreated: handleExtensionAppointmentCreated,
    onAppointmentError: handleExtensionAppointmentError,
    onAppointmentCancelled: handleExtensionAppointmentCancelled
  } = useMemo(
    () => bindAppointmentExtension(extension, extensionContext),
    [extension, extensionContext]
  );

  /**
   * Open / close lifecycle for the form and slot picker.
   *
   * **Open (first frame `visible` is true):** bumps `dialogKey` for date-picker internals, seeds
   * slot selection via `initializeForOpen`, then builds `formValues`: extension `getInitialValues`
   * first, then explicit `initialValues` fields (resource, times, guests, notes, appointment type).
   * Uses `useLayoutEffect` so fields are populated before paint and the resource select does not flash empty.
   *
   * **Close:** clears open snapshot, slot hook state, and the one-shot init flag so the next open starts clean.
   */
  useLayoutEffect(() => {
    if (visible) {
      if (!hasInitializedForOpenRef.current) {
        const openValues = initialValuesWhenOpenedRef.current;
        const explicitMode = openValues?.bookingMode;
        const shouldWaitForServices =
          servicesPending && (explicitMode === undefined || explicitMode === 'service');
        if (shouldWaitForServices) {
          return;
        }

        hasInitializedForOpenRef.current = true;
        setDialogKey(prev => prev + 1);
        const preferredDate = openValues?.preferredDate?.startOf('day');
        const initialMode = resolveInitialBookingMode(openValues?.bookingMode, showServiceModeOption);
        setBookingMode(initialMode);
        setPreferredDateWhenOpened(preferredDate);
        const slotInit = initialMode === 'service'
          ? serviceSlotSelection.initializeForOpen
          : initializeResourceSlotsForOpen;
        slotInit({
          preferredDate,
          initialStartTime: openValues?.startTime,
          committed: !!openValues?.startTime && !openValues?.preferredDate
        });

        const extensionInitialValues = getExtensionInitialValues();
        const formValues: Record<string, any> = {
          appointmentType: 'anonymous',
          createReminder: false,
          ...extensionInitialValues
        };
        const extDuration = formValues.duration;
        formValues.duration =
          openValues?.duration ??
          (typeof extDuration === 'number' && Number.isInteger(extDuration) && extDuration >= 5
            ? extDuration
            : DEFAULT_DURATION_MINUTES);

        if (openValues) {
          if (openValues.serviceId) {
            formValues.serviceId = openValues.serviceId;
          }
          if (openValues.primaryResourceId) {
            formValues.primaryResourceId = openValues.primaryResourceId;
          }
          if (openValues.resourceId) {
            formValues.resourceId = openValues.resourceId;
          }
          if (openValues.startTime) {
            formValues.startTime = openValues.startTime;
          }
          if (openValues.anonymousName) {
            formValues.anonymousName = openValues.anonymousName;
          }
          if (openValues.anonymousPhone) {
            formValues.anonymousPhone = openValues.anonymousPhone;
          }
          if (openValues.anonymousEmail) {
            formValues.anonymousEmail = openValues.anonymousEmail;
          }
          if (openValues.notes) {
            formValues.notes = openValues.notes;
          }
          if (openValues.anonymousName || openValues.anonymousPhone) {
            formValues.appointmentType = 'anonymous';
            setAppointmentType('anonymous');
          } else if (openValues.userId) {
            formValues.appointmentType = 'user';
            formValues.userId = openValues.userId;
            setAppointmentType('user');
          }
        }

        setDuration(formValues.duration);
        form.setFieldsValue(formValues);
      }
    } else {
      hasInitializedForOpenRef.current = false;
      hasCapturedOpenValuesRef.current = false;
      initialValuesWhenOpenedRef.current = undefined;
      setPreferredDateWhenOpened(undefined);
      setBookingMode('resource');
      resetForClose();
    }
  }, [
    visible,
    form,
    initializeResourceSlotsForOpen,
    serviceSlotSelection.initializeForOpen,
    resetForClose,
    showServiceModeOption,
    servicesPending
  ]);

  const handleBookingModeChange = useCallback((mode: AppointmentBookingMode) => {
    setBookingMode(mode);
    form.setFieldsValue({ startTime: null });
    setHasCommittedStartTime(false);
    if (mode === 'service') {
      serviceSlotSelection.resetForClose();
    } else {
      resetResourceSlotsForClose();
    }
  }, [form, setHasCommittedStartTime, serviceSlotSelection.resetForClose, resetResourceSlotsForClose]);

  const handleServiceChange = useCallback(() => {
    form.setFieldsValue({
      primaryResourceId: undefined,
      startTime: null
    });
    serviceSlotSelection.setHasCommittedStartTime(false);
  }, [form, serviceSlotSelection.setHasCommittedStartTime]);

  /**
   * Keeps `resourceId` consistent with loaded resources and caller intent.
   *
   * - If `initialValues` specified a resource that now exists in the list but the form is empty or
   *   holds a stale id, apply that id (covers slow resource fetch vs layout init).
   * - If there is no intended resource and no current value, default to the first bookable resource.
   */
  useEffect(() => {
    if (
      !visible
      || isServiceMode
      || !showResourceSelector
      || resourceSelectorLoading
      || !resources
      || resources.length === 0
    ) {
      return;
    }

    const intendedResourceId =
      initialValuesWhenOpenedRef.current?.resourceId ?? initialValues?.resourceId;
    const validIds = new Set(directBookableResources.map((r) => r.id));
    const currentResourceId = form.getFieldValue('resourceId');

    if (intendedResourceId && validIds.has(intendedResourceId)) {
      const missingOrInvalid = !currentResourceId || !validIds.has(currentResourceId);
      if (missingOrInvalid) {
        form.setFieldsValue({ resourceId: intendedResourceId });
        return;
      }
    }

    if (!currentResourceId || !validIds.has(currentResourceId)) {
      const firstResource = directBookableResources[0];
      if (firstResource?.id) {
        form.setFieldsValue({ resourceId: firstResource.id });
      } else if (currentResourceId) {
        form.setFieldsValue({ resourceId: undefined });
      }
    }
  }, [
    visible,
    isServiceMode,
    resources,
    directBookableResources,
    resourceSelectorLoading,
    showResourceSelector,
    form,
    initialValues
  ]);

  useEffect(() => {
    if (!visible || !isServiceMode || servicesLoading) {
      return;
    }

    if (appointmentServices.length === 0) {
      form.setFieldsValue({
        serviceId: undefined,
        primaryResourceId: undefined
      });
      handleBookingModeChange('resource');
      return;
    }

    const currentServiceId = form.getFieldValue('serviceId');
    const hasValidCurrentService = appointmentServices.some(
      (service) => service.id === currentServiceId
    );
    if (hasValidCurrentService) {
      return;
    }

    const intendedServiceId =
      initialValuesWhenOpenedRef.current?.serviceId ?? initialValues?.serviceId;
    if (
      intendedServiceId &&
      appointmentServices.some((service) => service.id === intendedServiceId)
    ) {
      form.setFieldsValue({
        serviceId: intendedServiceId,
        primaryResourceId: undefined,
        startTime: null
      });
      serviceSlotSelection.setHasCommittedStartTime(false);
      return;
    }

    form.setFieldsValue({
      serviceId: appointmentServices[0].id,
      primaryResourceId: undefined,
      startTime: null
    });
    serviceSlotSelection.setHasCommittedStartTime(false);
  }, [
    visible,
    isServiceMode,
    servicesLoading,
    appointmentServices,
    form,
    initialValues,
    handleBookingModeChange,
    serviceSlotSelection.setHasCommittedStartTime
  ]);

  useEffect(() => {
    if (!visible || !isServiceMode || !selectedService) {
      return;
    }
    form.setFieldsValue({ duration: selectedService.durationMinutes });
    setDuration(selectedService.durationMinutes);
  }, [visible, isServiceMode, selectedService, form]);

  /**
   * When the selected resource has a fixed slot length (`slotDurationMinutes`), align form duration
   * and local `duration` state with policy so the slot grid and API stay in sync.
   */
  useEffect(() => {
    if (visible && !isServiceMode && resourceId && resourcePolicy) {
      const policyDuration = resourcePolicy.slotDurationMinutes;
      if (policyDuration) {
        form.setFieldsValue({ duration: policyDuration });
        setDuration(policyDuration);
      }
    }
  }, [visible, isServiceMode, resourceId, resourcePolicy, form]);

  /**
   * Ant Design `onFinish` handler: validates slot state, runs module `validate`, builds the REST
   * payload (optional anonymous guest block, optional `context` / `metadata`), honors extension
   * interception (`skip`), then calls the create mutation. On success runs `onSuccess`, resets
   * optional local state, and closes via `onCancel`. Errors surface through `onError` and/or toast.
   *
   * @param values - Ant Design form values after submit (resource, `startTime`, duration, guest fields, notes).
   */
  const executeSubmit = async (values: {
    resourceId?: string;
    serviceId?: string;
    primaryResourceId?: string;
    startTime: Dayjs;
    duration: number;
    anonymousName?: string;
    anonymousPhone?: string;
    anonymousEmail?: string;
    notes?: string;
    companyId?: string;
    createReminder?: boolean;
    /** Registered customer id when booking for a user (appointment type `user`). */
    userId?: string;
    /** Form value of the format selector (`video` / `in-person`), mapped to the API format on submit. */
    appointmentFormat?: string;
    /** Optional title field contributed by module extensions; used to name the video room. */
    title?: string;
  }) => {
    const trimmedValues = deepTrimStrings(values);

    const startTime = trimmedValues.startTime;
    const rawDuration = trimmedValues.duration ?? duration;
    const durationMins = Number(rawDuration);
    const safeDuration = isServiceMode && selectedService
      ? selectedService.durationMinutes
      : (Number.isInteger(durationMins) && durationMins >= 5 && durationMins <= 480)
        ? durationMins
        : DEFAULT_DURATION_MINUTES;
    const endTime = startTime.add(safeDuration, 'minute');

    if (isServiceMode && showPrimaryResourceSelector && !serviceSlotSelection.slotsQueryReady) {
      showErrorMessage(t('appointmentComponents.create.primaryResource.required'));
      return;
    }

    if (isCheckingSelectedMonthSlots) {
      showErrorMessage(t('appointmentComponents.create.actions.checkingAvailability'));
      return;
    }

    if (!hasCommittedStartTime) {
      showErrorMessage(t('appointmentComponents.create.dateTime.required'));
      return;
    }

    if (truncated && !isSelectedSlotValid) {
      showErrorMessage(t('appointmentComponents.create.timeNotAvailable'));
      return;
    }

    if (displayedSlotUnavailable?.hasViolation || !isSelectedSlotValid) {
      showErrorMessage(t('appointmentComponents.create.timeNotAvailable'));
      return;
    }

    const extensionValidation = await validateExtension();
    if (!extensionValidation.valid) {
      showErrorMessage(extensionValidation.error || t('appointmentComponents.create.validationFailed'));
      if (extensionValidation.fieldErrors) {
        const fields = Object.keys(extensionValidation.fieldErrors).map(field => ({
          name: field,
          errors: [extensionValidation.fieldErrors![field]]
        }));
        form.setFields(fields);
      }
      return;
    }

    try {
      const guestPhone = toE164Phone(trimmedValues.anonymousPhone);
      const guestContact =
        appointmentType === 'anonymous'
          ? {
            ...(trimmedValues.anonymousName
              ? { anonymousName: String(trimmedValues.anonymousName).trim() }
              : {}),
            ...(guestPhone ? { anonymousPhone: guestPhone } : {}),
            ...(trimmedValues.anonymousEmail
              ? { anonymousEmail: String(trimmedValues.anonymousEmail).trim() }
              : {})
          }
          : {};

      const appointmentFormat = mapFormFormatToApi(trimmedValues.appointmentFormat);
      let meetingUrl: string | null = null;
      let meetingModeratorUrl: string | null = null;

      if (appointmentFormat === 'VIDEO') {
        if (!currentUser?.email) {
          showErrorMessage('You must be logged in to create a video meeting');
          return;
        }
        if (!effectiveClientId) {
          showErrorMessage('Select a client before creating a video meeting');
          return;
        }

        const application = (moduleData as { application?: { name?: string; email?: string; position?: string } } | undefined)
          ?.application;
        const memberEmail =
          application?.email ||
          (typeof trimmedValues.anonymousEmail === 'string' ? trimmedValues.anonymousEmail.trim() : undefined) ||
          'user@example.com';
        const memberName =
          application?.name ||
          (typeof trimmedValues.anonymousName === 'string' ? trimmedValues.anonymousName.trim() : undefined) ||
          'Member';
        const appointmentTitle =
          (typeof trimmedValues.title === 'string' ? trimmedValues.title.trim() : undefined) ||
          (application?.position ? `Interview-${application.position}` : undefined);
        const roomName = appointmentTitle
          ? `${appointmentTitle}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
          : undefined;

        setIsPreparingVideoLinks(true);
        try {
          const links = await generateJitsiMeetingLinks({
            clientId: effectiveClientId,
            ownerEmail: currentUser.email,
            ownerName: currentUser.email.split('@')[0] || 'Owner',
            memberEmail,
            memberName,
            roomName
          });
          meetingUrl = links.meetingUrl;
          meetingModeratorUrl = links.meetingModeratorUrl;
        } catch (error) {
          console.error('Failed to generate Jitsi links:', error);
          showErrorMessage('Failed to generate video meeting links. Please try again.');
          return;
        } finally {
          setIsPreparingVideoLinks(false);
        }
      }

      const meetingFields =
        appointmentFormat === 'VIDEO'
          ? {
              format: appointmentFormat,
              meetingUrl,
              meetingModeratorUrl
            }
          : {
              format: appointmentFormat as AppointmentFormat,
              meetingUrl: null,
              meetingModeratorUrl: null
            };

      const resolvedUserId =
        appointmentType === 'user'
          ? (typeof trimmedValues.userId === 'string' && trimmedValues.userId.trim()
              ? trimmedValues.userId.trim()
              : initialValues?.userId) || undefined
          : undefined;
      const registeredContactSnapshot =
        appointmentType === 'user' && selectedRegisteredCustomer
          ? {
            ...(selectedRegisteredCustomer.name?.trim()
              ? { anonymousName: selectedRegisteredCustomer.name.trim() }
              : {}),
            ...(selectedRegisteredCustomer.phoneNumber?.trim()
              ? { anonymousPhone: selectedRegisteredCustomer.phoneNumber.trim() }
              : {}),
            ...(selectedRegisteredCustomer.email?.trim()
              ? { anonymousEmail: selectedRegisteredCustomer.email.trim() }
              : {})
          }
          : {};

      if (isServiceMode && appointmentType === 'user' && resolvedUserId && serviceRequiredUserInfo) {
        const missingFields: string[] = [];
        if (serviceRequiredUserInfo.name && !registeredContactSnapshot.anonymousName) missingFields.push('name');
        if (serviceRequiredUserInfo.email && !registeredContactSnapshot.anonymousEmail) missingFields.push('email');
        if (serviceRequiredUserInfo.phone && !registeredContactSnapshot.anonymousPhone) missingFields.push('phone');
        if (missingFields.length > 0) {
          showErrorMessage(`Selected customer is missing required: ${missingFields.join(', ')}.`);
          return;
        }
      }

      const appointmentData: CreateClientAppointmentRequest | CreateAppointmentRequest | CreateClientServiceAppointmentRequest | CreateServiceAppointmentRequest = isServiceMode
        ? {
          startTime: startTime.toISOString(),
          notes: trimmedValues.notes || undefined,
          companyId: trimmedValues.companyId || initialValues?.companyId || undefined,
          userId: resolvedUserId,
          sessionId: initialValues?.sessionId || undefined,
          ...(trimmedValues.primaryResourceId
            ? { primaryResourceId: String(trimmedValues.primaryResourceId) }
            : {}),
          ...registeredContactSnapshot,
          ...guestContact,
          ...meetingFields
        }
        : {
          resourceId: trimmedValues.resourceId!,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          status: createAsConfirmed ? 'CONFIRMED' : undefined,
          notes: trimmedValues.notes || undefined,
          companyId: trimmedValues.companyId || initialValues?.companyId || undefined,
          userId: resolvedUserId,
          sessionId: initialValues?.sessionId || undefined,
          ...meetingFields,
          ...(appointmentType === 'anonymous' && trimmedValues.anonymousName && guestPhone
            ? {
              anonymousName: String(trimmedValues.anonymousName).trim(),
              anonymousPhone: guestPhone,
              ...(trimmedValues.anonymousEmail
                ? { anonymousEmail: String(trimmedValues.anonymousEmail).trim() }
                : {})
            }
            : {})
        };

      /** Optional `context` on the create payload when `moduleId` is set; built via extension `buildContext`. */
      let extensionContext: ParsedAppointmentContext | null = null;
      if (moduleId && buildExtensionContext) {
        const allCurrentFormValues = deepTrimStrings(form.getFieldsValue());
        const contextData = await buildExtensionContext(allCurrentFormValues);
        if (contextData) {
          const { source, ...data } = contextData;
          extensionContext = {
            source: source || moduleId,
            data: data
          };
        }
      }

      const appointmentDataWithContext = {
        ...appointmentData,
        ...(extensionContext ? { context: extensionContext } : {}),
        ...(metadata ? { metadata: metadata } : {})
      };

      const interception = await handleExtensionBeforeAppointmentCreated(appointmentDataWithContext);

      // Extension may skip the HTTP create (e.g. delegated agent flow) and return a synthetic result.
      if (interception.skip) {
        if (onSuccess) {
          onSuccess(interception.result || null);
        }
        if (resetOnClose) {
          form.resetFields();
          setDuration(DEFAULT_DURATION_MINUTES);
          setAppointmentType('anonymous');
          setBookingMode('resource');
        }
        onCancel();
        return;
      }

      const result = isServiceMode
        ? await createServiceAppointmentMutation.mutateAsync({
          serviceId: String(trimmedValues.serviceId),
          body: appointmentDataWithContext as CreateClientServiceAppointmentRequest | CreateServiceAppointmentRequest
        })
        : await createAppointmentMutation.mutateAsync(appointmentDataWithContext as CreateClientAppointmentRequest | CreateAppointmentRequest);

      if (!onSuccess) {
        showSuccessMessage(t('appointmentComponents.create.success'));
      }

      await handleExtensionAppointmentCreated(result);

      if (onSuccess) {
        onSuccess(result);
      }

      if (trimmedValues.createReminder) {
        const dueAt = dayjs(result?.startTime ?? startTime).subtract(15, 'minute').startOf('minute');
        const selectedResource = resources?.find((resource) => resource.id === (result?.resourceId ?? trimmedValues.resourceId));
        openReminderCreate({
          initialValues: {
            title: t('appointmentComponents.create.reminder.prefillTitle'),
            description: trimmedValues.notes || undefined,
            dueAt: dueAt.isAfter(dayjs()) ? dueAt : dayjs().add(5, 'minute').startOf('minute')
          },
          context: {
            source: 'appointment',
            appointmentId: result?.id,
            resourceId: result?.resourceId ?? trimmedValues.resourceId,
            startTime: result?.startTime ?? startTime.toISOString(),
            endTime: result?.endTime ?? endTime.toISOString(),
            appointment: result,
            resource: selectedResource || null,
            moduleId: moduleId || undefined,
            moduleData: moduleData || undefined
          }
        });
      }

      if (resetOnClose) {
        form.resetFields();
        setDuration(DEFAULT_DURATION_MINUTES);
        setAppointmentType('anonymous');
        setCreateAsConfirmed(false);
        setBookingMode('resource');
      }

      onCancel();
    } catch (error) {
      const message = getApiErrorMessage(error, t('appointmentComponents.create.error'));
      const errorObj = error instanceof Error ? error : new Error(message);

      if (!onError) {
        showErrorMessage(message);
      }
      console.error('Failed to create appointment:', error);

      await handleExtensionAppointmentError(errorObj);

      if (onError) {
        onError(errorObj);
      }
    }
  };

  const handleSubmit = async (
    values: Parameters<typeof executeSubmit>[0]
  ) => {
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    setIsSubmitWorkflowRunning(true);

    try {
      await executeSubmit(values);
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitWorkflowRunning(false);
    }
  };

  /**
   * Modal cancel / backdrop close: notifies the module extension, optionally resets the form and
   * related local state, then invokes parent `onCancel` (which typically clears the create session).
   */
  const handleCancel = () => {
    if (submitInFlightRef.current) {
      return;
    }
    handleExtensionAppointmentCancelled();
    
    if (resetOnClose) {
      form.resetFields();
      setDuration(DEFAULT_DURATION_MINUTES);
      setAppointmentType('anonymous');
      setCreateAsConfirmed(false);
      setBookingMode('resource');
    }
    onCancel();
  };

  /** Stacks the sections of one dialog column with a uniform gap. */
  const columnStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: token.marginLG
  };

  /**
   * Renders the modal shell (`destroyOnHidden` unmounts content when closed; `key` forces a clean
   * subtree when reopening). The inner Ant Design `Form` `initialValues` mirror the open snapshot ref
   * so resource and time fields paint correctly on the first frame.
   */
  return (
    <Modal
      key={visible ? 'open' : 'closed'}
      title={
        <Space size="small" align="center">
          <span
            style={{
              width: token.controlHeight,
              height: token.controlHeight,
              borderRadius: token.borderRadiusLG,
              background: token.colorPrimaryBg,
              color: token.colorPrimary,
              fontSize: token.fontSizeLG,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <CalendarOutlined />
          </span>
          <Text strong style={{ fontSize: token.fontSizeLG }}>
            {title || t('appointmentComponents.create.title')}
          </Text>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      closable={!isSubmitWorkflowRunning}
      keyboard={!isSubmitWorkflowRunning}
      mask={{ closable: !isSubmitWorkflowRunning }}
      footer={null}
      width={MODAL_WIDTH}
      centered
      destroyOnHidden
      styles={{
        body: {
          paddingTop: token.paddingSM
        }
      }}
    >
      <Space orientation="vertical" size="small" style={{ width: '100%' }}>
        <ConflictAlerts
          slotUnavailable={displayedSlotUnavailable}
          isCheckingSlots={isCheckingSlots}
          truncated={truncated}
          resourceId={isServiceMode ? serviceId : resourceId}
          hasCommittedStartTime={hasCommittedStartTime}
          onDismissSlotUnavailable={() => setDismissedSlotUnavailable(true)}
        />

        {/*
         * Form `initialValues` mirror the open snapshot ref and policy-derived duration so the first
         * paint matches `useLayoutEffect`; keep in sync when adding fields here.
         */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={isSubmitWorkflowRunning}
          requiredMark="optional"
          initialValues={{
            duration: resourcePolicy?.slotDurationMinutes ?? initialValuesWhenOpenedRef.current?.duration ?? initialValues?.duration ?? DEFAULT_DURATION_MINUTES,
            appointmentType: 'anonymous',
            createReminder: false,
            resourceId:
              initialValuesWhenOpenedRef.current?.resourceId ?? initialValues?.resourceId,
            startTime: initialValuesWhenOpenedRef.current?.startTime,
            appointmentFormat: 'in-person'
          }}
        >
          <Row gutter={[token.marginXL, token.marginLG]}>
            {/* Left column: what is booked and when */}
            <Col xs={24} lg={12} style={columnStyle}>
              <FormSection
                title={t('appointmentComponents.create.sections.booking')}
                icon={<AppstoreOutlined />}
              >
                <BookingModeSelector
                  showServiceMode={showServiceModeOption}
                  value={bookingMode}
                  onChange={handleBookingModeChange}
                />

                {isServiceMode && !showServiceModeOption ? <ServiceBookingEmptyAlert /> : null}

                {/* Module extension: UI injected before the resource row */}
                {getSlotComponent(AppointmentDialogSlot.BEFORE_RESOURCE)}

                {isServiceMode ? (
                  <>
                    <ServiceSelector
                      services={appointmentServices}
                      loading={servicesLoading}
                      onChange={handleServiceChange}
                    />
                    <ServiceBookingSummary service={selectedService} />
                    {showPrimaryResourceSelector ? (
                      <PrimaryResourceSelector service={selectedService} />
                    ) : null}
                  </>
                ) : (
                  <>
                    {showDirectResourceBookingEmpty ? <DirectResourceBookingEmptyAlert /> : null}
                    <Row gutter={[token.marginSM, 0]}>
                      {showResourceSelector && (
                        <Col xs={24} sm={12}>
                          <ResourceSelector
                            resources={directBookableResources}
                            loading={resourceSelectorLoading}
                          />
                        </Col>
                      )}
                      <Col xs={24} sm={showResourceSelector ? 12 : 24}>
                        <DurationInput
                          resourcePolicy={resourcePolicy}
                          duration={duration}
                          onDurationChange={(value) => setDuration(value ?? DEFAULT_DURATION_MINUTES)}
                        />
                      </Col>
                    </Row>
                  </>
                )}

                {/* Module extension: UI after duration, before the slot date/time picker */}
                {getSlotComponent(AppointmentDialogSlot.AFTER_DURATION)}
              </FormSection>

              <FormSection
                title={t('appointmentComponents.create.sections.dateTime')}
                icon={<ClockCircleOutlined />}
              >
                <SlotDateTimePicker
                  resourcePolicy={resourcePolicy}
                  duration={effectiveDuration}
                  form={form}
                  dialogKey={dialogKey}
                  useSlotSchedule
                  disabledDate={disabledDate}
                  disabledTime={slotPickerDisabledTime}
                  slotAlignmentMinutes={slotAlignmentMinutes}
                  visibleMonth={slotsVisibleMonth}
                  onVisibleMonthChange={handleSlotsVisibleMonthChange}
                  preferredPickerValue={preferredPickerValue ?? effectivePreferredDate}
                  onStartTimeCommit={setHasCommittedStartTime}
                  onCalendarDaySelect={handleCalendarDaySelect}
                  label={t('appointmentComponents.create.dateTime.label')}
                  slotsByDateKey={slotsByDateKey}
                  timezone={timezone}
                  isLoadingSlots={isLoadingVisibleMonthSlots}
                  slotsTruncated={truncated}
                  displayMode="inline"
                />

                <TimePreview duration={effectiveDuration} />
              </FormSection>
            </Col>

            {/* Right column: who the appointment is for and extra details */}
            <Col xs={24} lg={12} style={columnStyle}>
              <FormSection
                title={t('appointmentComponents.create.sections.guest')}
                icon={<UserOutlined />}
              >
                {showAppointmentType && (
                  <AppointmentTypeSelector
                    appointmentType={appointmentType}
                    onTypeChange={(type) => {
                      setAppointmentType(type);
                      if (type === 'anonymous') {
                        form.setFieldsValue({ userId: undefined });
                      } else {
                        form.setFieldsValue({
                          anonymousName: undefined,
                          anonymousPhone: undefined,
                          anonymousEmail: undefined
                        });
                      }
                    }}
                  />
                )}

                {/* Module extension: UI after appointment type, before guest details */}
                {getSlotComponent(AppointmentDialogSlot.AFTER_APPOINTMENT_TYPE)}

                {showAppointmentType && (
                  <RegisteredCustomerSelector appointmentType={appointmentType} />
                )}

                <GuestDetails
                  appointmentType={appointmentType}
                  requiredUserInfo={isServiceMode ? serviceRequiredUserInfo : undefined}
                />
              </FormSection>

              <FormSection
                title={t('appointmentComponents.create.sections.details')}
                icon={<FileTextOutlined />}
              >
                <AppointmentFormatSelector
                  label={t('appointmentComponents.create.format.label')}
                  requiredMessage={t('appointmentComponents.create.format.required')}
                />

                {/* Module extension: UI before the notes field */}
                {getSlotComponent(AppointmentDialogSlot.BEFORE_NOTES)}

                {showNotes && (
                  <NotesField required={isServiceMode && (serviceRequiredUserInfo?.notes ?? false)} />
                )}

                <ReminderToggle />

                {/* Module extension: UI after the notes field */}
                {getSlotComponent(AppointmentDialogSlot.AFTER_NOTES)}
              </FormSection>
            </Col>
          </Row>

          <FormActions
            onCancel={handleCancel}
            isSubmitting={
              isSubmitWorkflowRunning ||
              isPreparingVideoLinks ||
              createAppointmentMutation.isPending ||
              createServiceAppointmentMutation.isPending
            }
            showConfirmSwitch={showConfirmSwitch && !isServiceMode}
            createAsConfirmed={createAsConfirmed}
            onCreateAsConfirmedChange={setCreateAsConfirmed}
            isCheckingSlots={isCheckingSlots}
            slotUnavailable={displayedSlotUnavailable}
            truncated={truncated}
            hasCommittedStartTime={hasCommittedStartTime}
          />
        </Form>
      </Space>
    </Modal>
  );
};

export default CreateAppointmentDialog;
export type { CreateAppointmentDialogProps, CreateAppointmentDialogInitialValues, CreateAppointmentDialogMetadata } from './types';
